import { Box, SegmentedControl } from '@mantine/core';
import { ReactElement } from 'react';
import { useSearchParams } from 'react-router';
import { ImportsView } from './imports/ImportsView';
import { LedgerView } from './ledger/LedgerView';
import { RefundsView } from './refunds/RefundsView';
import { useRefundWorklists } from './refunds/useRefundWorklists';
import { WORK_COUNT_STYLE } from './workCount';

export type RevenueView = 'ledger' | 'imports' | 'refunds';
const DEFAULT_VIEW: RevenueView = 'ledger';

const VIEW_OPTIONS: { label: string; value: RevenueView }[] = [
  { label: 'Ledger', value: 'ledger' },
  { label: 'Imports', value: 'imports' },
  { label: 'Refunds', value: 'refunds' },
];

function isValidView(value: string | null): value is RevenueView {
  return VIEW_OPTIONS.some((option) => option.value === value);
}

export function RevenueTab(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const activeView = isValidView(viewParam) ? viewParam : DEFAULT_VIEW;
  const refunds = useRefundWorklists();
  const refundWork = refunds.requests.length + refunds.refunds.length;
  const options = VIEW_OPTIONS.map((option) =>
    option.value === 'refunds' && refundWork > 0
      ? {
          value: option.value,
          label: (
            <span>
              {option.label}
              <span style={WORK_COUNT_STYLE}>{refundWork}</span>
            </span>
          ),
        }
      : option
  );

  const handleViewChange = (value: string): void => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('view', value);
      return next;
    });
  };

  return (
    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box style={{ padding: '12px 16px 0' }}>
        <SegmentedControl size="xs" data={options} value={activeView} onChange={handleViewChange} />
      </Box>
      <Box
        mt={12}
        style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        data-testid={`revenue-view-${activeView}`}
      >
        {activeView === 'ledger' && <LedgerView />}
        {activeView === 'imports' && <ImportsView />}
        {activeView === 'refunds' && <RefundsView worklists={refunds} />}
      </Box>
    </Box>
  );
}
